import { useEffect, useState } from 'react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';
import { toast } from 'sonner';

/**
 * La fiche d'un produit ajouté par le SAV du distributeur : création ou modification.
 *
 * Call avec David du 01/10 : « un produit qu'on n'a pas référencé, des ampoules… il fera
 * son petit CRM ». Mêmes informations que les produits enregistrés par CFT Sign ou
 * Flexcore, sans chantier obligatoire (arbitrage de Boubacar).
 *
 * Le TYPE se choisit dans la liste du sous-compte OU se saisit : un type nouveau rejoint
 * la liste pour tout le sous-compte, c'est le serveur qui le décide (casse ignorée).
 * Ce produit n'est jamais pilotable : il n'entre ni dans le Monitoring ni dans le
 * Pilotage, et la fiche le dit pour que personne ne l'attende là.
 */
const VIDE = {
  type: '', marque: '', modele: '', numero_serie: '', puissance_kw: '',
  date_installation: '', reference_chantier: '', commentaire: '',
};

export default function FicheProduitDialog({ ouvert, produit = null, types = [], onFerme, onEnregistre }) {
  const [form, setForm] = useState(VIDE);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!ouvert) return;
    setForm(produit
      ? Object.fromEntries(Object.keys(VIDE).map(k => [k, produit[k] ?? '']))
      : VIDE);
  }, [ouvert, produit]);

  const champ = (cle) => ({
    value: form[cle],
    onChange: (e) => setForm(f => ({ ...f, [cle]: e.target.value })),
  });

  const enregistrer = async (e) => {
    e.preventDefault();
    if (!form.type.trim()) { toast.error('Le type est obligatoire'); return; }
    const corps = {
      ...form,
      puissance_kw: form.puissance_kw === '' ? null : Number(form.puissance_kw),
      date_installation: form.date_installation || null,
    };
    setEnvoi(true);
    try {
      const r = produit
        ? await api.put(`/distributeur/produits/${produit.id}`, corps)
        : await api.post('/distributeur/produits', corps);
      toast.success(produit ? 'Produit modifié' : 'Produit ajouté');
      onEnregistre?.(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail[0]?.msg || 'Saisie refusée' : "Le produit n'a pas été enregistré");
    }
    setEnvoi(false);
  };

  return (
    <Dialog open={ouvert} onOpenChange={o => { if (!o) onFerme?.(); }}>
      <DialogContent className="max-w-lg" data-testid="fiche-produit-dialog">
        <DialogHeader>
          <DialogTitle>{produit ? 'Modifier le produit' : 'Ajouter un produit'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="space-y-3 text-[13px]">
          <div>
            <Label className="text-xs text-neutral-500">Type de produit *</Label>
            <Input list="types-produits" placeholder="Choisir ou saisir un nouveau type"
                   {...champ('type')} data-testid="produit-type" required />
            <datalist id="types-produits">
              {types.map(t => <option key={t} value={t} />)}
            </datalist>
            <p className="text-[11px] text-neutral-400 mt-1">
              Un type absent de la liste y est ajouté pour tout votre espace.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-neutral-500">Marque</Label>
              <Input {...champ('marque')} data-testid="produit-marque" /></div>
            <div><Label className="text-xs text-neutral-500">Modèle</Label>
              <Input {...champ('modele')} data-testid="produit-modele" /></div>
            <div><Label className="text-xs text-neutral-500">N° de série</Label>
              <Input {...champ('numero_serie')} data-testid="produit-serie" /></div>
            <div><Label className="text-xs text-neutral-500">Puissance (kW)</Label>
              <Input type="number" min="0" step="any" {...champ('puissance_kw')} data-testid="produit-puissance" /></div>
            <div><Label className="text-xs text-neutral-500">Date de pose</Label>
              <Input type="date" {...champ('date_installation')} data-testid="produit-date" /></div>
            <div><Label className="text-xs text-neutral-500">Réf. chantier (facultatif)</Label>
              <Input {...champ('reference_chantier')} data-testid="produit-chantier" /></div>
          </div>
          <div>
            <Label className="text-xs text-neutral-500">Commentaire</Label>
            <textarea {...champ('commentaire')} rows={3} maxLength={2000}
                      className="w-full border border-neutral-200 rounded-md px-3 py-2 text-[13px]"
                      data-testid="produit-commentaire" />
          </div>
          <p className="text-[11px] text-neutral-400">
            Suivi SAV uniquement : ce produit n&apos;est relié à aucun canal et ne se pilote pas.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onFerme}>Annuler</Button>
            <Button type="submit" disabled={envoi} data-testid="produit-enregistrer">
              {envoi ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
